import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, ArrowLeft, Mail, Globe, ExternalLink } from 'lucide-react';

const SectionTitle: React.FC<{ number: string; children: React.ReactNode }> = ({
  number,
  children,
}) => (
  <h2 className="flex items-baseline gap-3 text-xl md:text-2xl font-bold text-partrunner-black dark:text-white mt-12 mb-4">
    <span className="text-partrunner-yellow-accent font-extrabold">{number}.</span>
    {children}
  </h2>
);

const BulletList: React.FC<{ items: string[] }> = ({ items }) => (
  <ul className="space-y-2 ml-1">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-3 text-partrunner-gray-dark dark:text-gray-300">
        <span className="mt-2 w-1.5 h-1.5 rounded-full bg-partrunner-yellow-accent shrink-0" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const SubSection: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div className="mb-6">
    <h3 className="font-semibold text-partrunner-black dark:text-white mb-2">{title}</h3>
    {children}
  </div>
);

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="min-h-screen bg-partrunner-bg-main dark:bg-gray-950">
      {/* Header */}
      <header className="relative bg-partrunner-yellow overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 py-8 pb-16">
          <div className="flex items-center justify-between mb-8">
            <img src="/images/logo-full-black.png" alt="Partrunner" className="h-10 w-auto" />
            <Link
              to="/portal/dashboard"
              className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium bg-white/30 text-partrunner-black border border-white/50 hover:bg-white/50 transition-colors"
            >
              <ArrowLeft size={16} />
              <span className="hidden sm:inline">Volver al inicio</span>
            </Link>
          </div>

          <div className="text-center max-w-2xl mx-auto">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/30 rounded-2xl mb-4">
              <Shield size={32} className="text-partrunner-black" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-partrunner-black mb-3">
              Política de Privacidad
            </h1>
            <p className="text-partrunner-black/70 text-lg">
              Integración de Transporte | PartRunner
            </p>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="relative max-w-4xl mx-auto px-4 -mt-8">
        <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-card p-6 md:p-10 lg:p-14 mb-12">
          {/* Meta info */}
          <div className="flex flex-wrap gap-4 text-sm text-partrunner-gray-dark dark:text-gray-400 mb-8 pb-6 border-b border-partrunner-gray-light dark:border-gray-800">
            <span>Última actualización: 4 de marzo de 2026</span>
            <span className="hidden sm:inline">·</span>
            <span>Vigente desde su publicación</span>
          </div>

          <p className="text-partrunner-gray-dark dark:text-gray-300 leading-relaxed mb-8">
            La presente Política de Privacidad describe cómo{' '}
            <strong className="text-partrunner-black dark:text-white">
              PartRunner Technologies S.A.P.I. de C.V.
            </strong>{' '}
            ("PartRunner", "nosotros") recopila, utiliza, almacena y protege la información personal
            de los usuarios ("usted", "usuario") en relación con nuestra integración de servicios de
            transporte bajo demanda a través de proveedores terceros, incluyendo la plataforma Uber.
          </p>
          <p className="text-partrunner-gray-dark dark:text-gray-300 leading-relaxed mb-8">
            Esta política cumple con la{' '}
            <strong className="text-partrunner-black dark:text-white">
              Ley Federal de Protección de Datos Personales en Posesión de los Particulares
              (LFPDPPP)
            </strong>{' '}
            y su Reglamento, así como con los lineamientos del INAI y las políticas de datos de Uber
            Technologies, Inc.
          </p>

          {/* 1 */}
          <SectionTitle number="1">Responsable del Tratamiento</SectionTitle>
          <div className="bg-partrunner-yellow-light dark:bg-yellow-900/20 rounded-2xl p-5 space-y-1 text-partrunner-gray-dark dark:text-gray-300">
            <p className="font-semibold text-partrunner-black dark:text-white">
              PartRunner Technologies S.A.P.I. de C.V.
            </p>
            <p>Domicilio: Ciudad de México, México</p>
            <p className="flex items-center gap-2">
              <Mail size={14} className="text-partrunner-yellow-accent" />
              Contacto de privacidad:{' '}
              <a
                href="mailto:privacy@partrunner.com"
                className="text-partrunner-yellow-accent hover:underline font-medium"
              >
                privacy@partrunner.com
              </a>
            </p>
          </div>

          {/* 2 */}
          <SectionTitle number="2">Datos que Recopilamos</SectionTitle>
          <p className="text-partrunner-gray-dark dark:text-gray-300 mb-6">
            A través de la integración con Uber, podemos acceder a los siguientes datos personales
            con su consentimiento explícito:
          </p>

          <SubSection title="Perfil (scope: profile)">
            <BulletList
              items={[
                'Nombre, correo electrónico, número de teléfono, foto de perfil, calificación de usuario',
              ]}
            />
          </SubSection>
          <SubSection title="Historial de viajes (scope: history)">
            <BulletList
              items={['Origen, destino, fecha/hora, distancia, duración, tarifa, tipo de servicio']}
            />
          </SubSection>
          <SubSection title="Solicitudes de viaje (scope: request)">
            <BulletList
              items={[
                'Ubicación de recogida/destino, estimaciones de precio/tiempo, estado del viaje, datos del conductor asignado',
              ]}
            />
          </SubSection>
          <SubSection title="Recibos (scope: request.receipt)">
            <BulletList
              items={['Desglose de tarifa, método de pago (parcial), propina, cargos adicionales']}
            />
          </SubSection>
          <SubSection title="Ubicación">
            <BulletList
              items={[
                'Coordenadas de recogida y destino proporcionadas por el usuario al solicitar viajes',
              ]}
            />
          </SubSection>

          <div className="bg-partrunner-green/10 dark:bg-green-900/20 border border-partrunner-green/20 rounded-2xl p-5 mt-4">
            <p className="text-partrunner-gray-dark dark:text-gray-300 text-sm">
              <strong className="text-partrunner-black dark:text-white">No recopilamos</strong>{' '}
              datos biométricos, información financiera completa (números de tarjeta), ni datos de
              salud.
            </p>
          </div>

          {/* 3 */}
          <SectionTitle number="3">Finalidades del Tratamiento</SectionTitle>

          <SubSection title="Finalidades primarias (necesarias para el servicio):">
            <BulletList
              items={[
                'Solicitar viajes de transporte bajo demanda en su nombre',
                'Mostrar estimaciones de precio y tiempo para toma de decisiones',
                'Facilitar el seguimiento en tiempo real de viajes activos',
                'Gestionar el historial de viajes solicitados a través de nuestra plataforma',
                'Proporcionar recibos y comprobantes de viaje',
              ]}
            />
          </SubSection>

          <SubSection title="Finalidades secundarias (con su consentimiento):">
            <BulletList
              items={[
                'Análisis de patrones de transporte para optimización de rutas corporativas',
                'Generación de reportes de gastos de transporte',
                'Mejora continua de nuestros servicios y experiencia de usuario',
              ]}
            />
          </SubSection>

          <p className="text-partrunner-gray-dark dark:text-gray-300 text-sm italic">
            Si no desea que sus datos se utilicen para finalidades secundarias, puede manifestarlo
            enviando un correo a{' '}
            <a
              href="mailto:privacy@partrunner.com"
              className="text-partrunner-yellow-accent hover:underline font-medium not-italic"
            >
              privacy@partrunner.com
            </a>
            .
          </p>

          {/* 4 */}
          <SectionTitle number="4">Base Legal</SectionTitle>
          <BulletList
            items={[
              'Consentimiento explícito: Usted autoriza el acceso a sus datos de Uber mediante el flujo de autenticación OAuth 2.0, donde se le muestran los permisos específicos antes de otorgarlos.',
              'Relación contractual: El tratamiento de datos es necesario para la prestación del servicio solicitado.',
              'Interés legítimo: Análisis agregado y anonimizado para mejora del servicio.',
            ]}
          />

          {/* 5 */}
          <SectionTitle number="5">Transferencias de Datos</SectionTitle>
          <p className="text-partrunner-gray-dark dark:text-gray-300 mb-4">
            Sus datos pueden ser compartidos con:
          </p>
          <BulletList
            items={[
              'Uber Technologies, Inc. — Necesaria para la ejecución del servicio de transporte. Uber actúa como procesador independiente bajo sus propias políticas de privacidad.',
              'Proveedores de infraestructura — Servicios de almacenamiento en la nube (Supabase/AWS) con cifrado en tránsito y reposo.',
            ]}
          />
          <div className="bg-partrunner-yellow-light dark:bg-yellow-900/20 border border-partrunner-yellow/30 rounded-2xl p-5 mt-4">
            <p className="text-partrunner-gray-dark dark:text-gray-300 text-sm font-medium">
              No vendemos, alquilamos ni compartimos sus datos personales con terceros para fines de
              marketing.
            </p>
          </div>

          {/* 6 */}
          <SectionTitle number="6">Almacenamiento y Seguridad</SectionTitle>
          <BulletList
            items={[
              'Los tokens de acceso se almacenan cifrados y se renuevan automáticamente',
              'Los datos de viaje se almacenan en servidores con cifrado AES-256 en reposo',
              'Todas las comunicaciones utilizan TLS 1.3',
              'Acceso restringido mediante principio de mínimo privilegio',
              'Retención: datos de viaje por 24 meses, tokens de sesión por 30 días',
            ]}
          />

          {/* 7 */}
          <SectionTitle number="7">Derechos ARCO</SectionTitle>
          <p className="text-partrunner-gray-dark dark:text-gray-300 mb-4">
            De conformidad con la LFPDPPP, usted tiene derecho a:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
            {[
              { title: 'Acceso', desc: 'Conocer qué datos personales tenemos sobre usted' },
              {
                title: 'Rectificación',
                desc: 'Solicitar la corrección de datos inexactos o incompletos',
              },
              { title: 'Cancelación', desc: 'Solicitar la eliminación de sus datos personales' },
              {
                title: 'Oposición',
                desc: 'Oponerse al tratamiento de sus datos para finalidades secundarias',
              },
            ].map(right => (
              <div key={right.title} className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
                <p className="font-semibold text-partrunner-black dark:text-white text-sm mb-1">
                  {right.title}
                </p>
                <p className="text-partrunner-gray-dark dark:text-gray-400 text-sm">{right.desc}</p>
              </div>
            ))}
          </div>
          <p className="text-partrunner-gray-dark dark:text-gray-300 text-sm">
            Para ejercer estos derechos, envíe un correo a{' '}
            <a
              href="mailto:privacy@partrunner.com"
              className="text-partrunner-yellow-accent hover:underline font-medium"
            >
              privacy@partrunner.com
            </a>{' '}
            con el asunto "Solicitud ARCO" incluyendo: nombre completo, descripción del derecho a
            ejercer, y una copia de identificación oficial.
          </p>
          <p className="text-partrunner-gray-dark dark:text-gray-300 text-sm mt-2">
            Responderemos en un plazo máximo de{' '}
            <strong className="text-partrunner-black dark:text-white">20 días hábiles</strong>{' '}
            conforme a la ley.
          </p>

          {/* 8 */}
          <SectionTitle number="8">Revocación del Consentimiento</SectionTitle>
          <p className="text-partrunner-gray-dark dark:text-gray-300 mb-4">
            Usted puede revocar en cualquier momento el acceso de PartRunner a sus datos de Uber:
          </p>
          <BulletList
            items={[
              'Desde la app de Uber: Configuración → Privacidad → Apps conectadas',
              'Enviando un correo a privacy@partrunner.com',
              'Solicitándolo directamente a través de nuestros canales de atención',
            ]}
          />
          <p className="text-partrunner-gray-dark dark:text-gray-300 text-sm mt-4 italic">
            La revocación no afecta la licitud del tratamiento realizado previamente.
          </p>

          {/* 9 */}
          <SectionTitle number="9">Cookies y Tecnologías de Rastreo</SectionTitle>
          <p className="text-partrunner-gray-dark dark:text-gray-300">
            Esta integración no utiliza cookies ni tecnologías de rastreo adicionales. La
            autenticación se realiza exclusivamente mediante tokens OAuth 2.0.
          </p>

          {/* 10 */}
          <SectionTitle number="10">Menores de Edad</SectionTitle>
          <p className="text-partrunner-gray-dark dark:text-gray-300">
            Nuestros servicios están dirigidos a personas mayores de 18 años. No recopilamos
            intencionalmente datos de menores de edad.
          </p>

          {/* 11 */}
          <SectionTitle number="11">Cambios a esta Política</SectionTitle>
          <p className="text-partrunner-gray-dark dark:text-gray-300">
            Nos reservamos el derecho de modificar esta política. Las actualizaciones serán
            publicadas en esta misma URL con la nueva fecha de vigencia. En caso de cambios
            sustanciales, le notificaremos por correo electrónico.
          </p>

          {/* 12 */}
          <SectionTitle number="12">Contacto</SectionTitle>
          <p className="text-partrunner-gray-dark dark:text-gray-300 mb-4">
            Para cualquier duda, comentario o solicitud relacionada con esta política de privacidad:
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href="mailto:privacy@partrunner.com"
              className="inline-flex items-center gap-2 px-5 py-3 bg-partrunner-yellow hover:bg-partrunner-yellow-dark text-partrunner-black font-semibold rounded-xl transition-colors"
            >
              <Mail size={18} />
              privacy@partrunner.com
            </a>
            <a
              href="https://partrunner.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-3 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-partrunner-black dark:text-white font-semibold rounded-xl transition-colors"
            >
              <Globe size={18} />
              partrunner.com
            </a>
          </div>

          {/* 13 */}
          <SectionTitle number="13">Autoridad Competente</SectionTitle>
          <p className="text-partrunner-gray-dark dark:text-gray-300 mb-3">
            Si considera que su derecho a la protección de datos personales ha sido vulnerado, puede
            presentar una queja ante el Instituto Nacional de Transparencia, Acceso a la Información
            y Protección de Datos Personales (INAI):
          </p>
          <a
            href="https://home.inai.org.mx"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-partrunner-yellow-accent hover:underline font-medium"
          >
            <ExternalLink size={14} />
            home.inai.org.mx
          </a>
        </div>

        {/* Footer */}
        <footer className="text-center pb-10 text-sm text-partrunner-gray-dark dark:text-gray-500">
          <div className="flex items-center justify-center gap-2 mb-2">
            <img src="/images/icon-color.png" alt="" className="h-5 w-5" />
            <span className="font-semibold text-partrunner-black dark:text-white">
              PartRunner Technologies S.A.P.I. de C.V.
            </span>
          </div>
          <p>&copy; 2026 Todos los derechos reservados.</p>
        </footer>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
